import { useContext, useEffect, useState } from 'react';

import PhoneInput from 'components/signup-input/phone-input';
import { AuthContext } from 'contexts/auth';
import { sendGTMEvent } from 'helpers/google-tag-manager';
import { AsYouType } from 'libphonenumber-js';
import { bool, func, shape } from 'prop-types';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { customMutators } from 'utils/old-ui-commons/form';
import InputLabelError from 'utils/old-ui-commons/input-label-error';
import {
  BR_PHONE_PATTERN,
  MX_PHONE_PATTERN,
  US_PHONE_PATTERN,
  defaultInitialPhoneValues,
  getParsedPhoneDetails,
  getPhoneInformationObject,
} from 'utils/phone-patterns';

import { Box, Flex } from '@olist/united';

import { AlertContext } from '../../../contexts/alert-context';
import Api from '../../../service';
import { CPF_MASK, PHONE_PATTERN, formValidate } from './validator';

const InputWrapper = (props) => (
  <Box width={1} maxWidth="352px" {...props} minHeight={7} mb={4}>
    <InputLabelError showMessages={false} {...props} />
  </Box>
);

const LiablePartnerForm = ({
  initialValues,
  isGtmLoaded,
  setIsCheckingSellerTag,
  setSubmitCallback,
}) => {
  const { t } = useTranslation();

  const { setAlert } = useContext(AlertContext);
  const { userInfo } = useContext(AuthContext);

  const phoneNumberParsed = getParsedPhoneDetails(initialValues.mobile_phone);

  const [phoneCountry, setPhoneCountry] = useState(defaultInitialPhoneValues);

  useEffect(() => {
    if (userInfo?.email && isGtmLoaded) {
      sendGTMEvent('page_view', { user_id: userInfo.email });
    }
  }, [userInfo?.email, isGtmLoaded]);

  useEffect(() => {
    const parsedPhone = getParsedPhoneDetails(initialValues.mobile_phone);

    setPhoneCountry({
      prefixNumber: parsedPhone.prefixNumber,
      code: parsedPhone.code,
      mask: parsedPhone.mask,
      placeHolder: parsedPhone.placeHolder,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitHandler = async (postValues) => {
    const phoneInfor = getPhoneInformationObject(postValues.mobile_phone, phoneCountry);

    const newValues = { ...postValues };
    newValues.mobile_phone = phoneInfor.getNumber().number;

    try {
      const responseValues = await Api.postLiablePartner(newValues);
      if (responseValues) {
        sendGTMEvent('etapa_cadastro', {
          etapa: 'dados_socio',
        });
        setIsCheckingSellerTag(true);
      }
    } catch (error) {
      setAlert({
        text: t`An error occurred and the information could not be sent. Try again.`,
        show: true,
        iconName: 'alert-triangle-outline',
      });
    }
  };

  const inputWidths = ['100%', '100%', '100%', '50%'];

  const handleChangePhoneCountry = (phoneParams) => {
    new AsYouType().reset();
    setPhoneCountry(phoneParams);
  };

  const handleOnChangePhoneNumber = (args, state, utils) => {
    const input = args[0]?.target?.value;
    const phone = new AsYouType(phoneCountry.code).input(input);
    utils.changeValue(state, 'mobile_phone', () => phone);
  };

  const handleDropdownItemsOptions = (value, prefixNumber, code, placeHolder, mask, form) => ({
    value,
    onClick: () => {
      form.mutators.setValue('mobile_phone', '');
      handleChangePhoneCountry({
        prefixNumber,
        code,
        mask,
        placeHolder,
      });
    },
  });

  const copyValues = { ...initialValues };
  copyValues.mobile_phone = phoneNumberParsed?.nationalNumber
    ? phoneNumberParsed.nationalNumber
    : initialValues.mobile_phone;

  return (
    <Form
      onSubmit={onSubmitHandler}
      initialValues={copyValues}
      validate={formValidate}
      mutators={{
        setValue: customMutators.setValue,
        handleOnChangePhoneNumber,
      }}
      render={({ form, handleSubmit, hasValidationErrors, input, submitting }) => {
        setSubmitCallback(handleSubmit, !hasValidationErrors, submitting);
        return (
          <form onSubmit={handleSubmit}>
            <Flex flexDirection={['column', 'column', 'column', 'row']} flex={2}>
              <InputWrapper
                mr={3}
                width={inputWidths}
                name="email"
                label={t`e-mail`}
                disabled
                {...input}
              />
              <Flex width={inputWidths} />
            </Flex>
            <Flex flexDirection={['column', 'column', 'column', 'row']}>
              <InputWrapper
                mr={3}
                width={inputWidths}
                label={t`account owner full name`}
                name="name"
                placeholder={t`type the name`}
              />
              <InputWrapper
                width={inputWidths}
                label={t`cpf`}
                mask={CPF_MASK}
                name="cpf"
                placeholder="___.___.___-__"
              />
            </Flex>
            <Flex
              flexDirection={['column', 'column', 'column', 'row']}
              justifyContent="space-between"
            >
              <Box mb={3} width={inputWidths} pr={[0, 3, 3]}>
                <PhoneInput
                  label={t`mobile phone number`}
                  id="mobile_country_code"
                  name="mobile_country_code"
                  defaultValue={phoneCountry.prefixNumber}
                  items={[
                    handleDropdownItemsOptions(
                      '+55 Brasil',
                      '+55',
                      'BR',
                      '(99) 99999-9999',
                      BR_PHONE_PATTERN,
                      form
                    ),
                    handleDropdownItemsOptions(
                      '+52 Mexico',
                      '+52',
                      'MX',
                      '999 999 9999',
                      MX_PHONE_PATTERN,
                      form
                    ),
                    handleDropdownItemsOptions(
                      '+1 Usa',
                      '+1',
                      'US',
                      '(999) 999-9999',
                      US_PHONE_PATTERN,
                      form
                    ),
                  ]}
                  handleOnChangeInput={form.mutators.handleOnChangePhoneNumber}
                  phoneValue={phoneCountry}
                />
              </Box>

              <InputWrapper
                ml={[0, 0, 3]}
                width={inputWidths}
                label={t`commercial phone number`}
                mask={PHONE_PATTERN}
                name="phone"
                placeholder="(__) _____-____"
                type="tel"
              />
            </Flex>
          </form>
        );
      }}
    />
  );
};

LiablePartnerForm.propTypes = {
  setSubmitCallback: func.isRequired,
  updatePersonalData: func.isRequired,
  goToNextStep: func.isRequired,
  initialValues: shape({}),
  setIsCheckingSellerTag: func.isRequired,
  isGtmLoaded: bool.isRequired,
};

LiablePartnerForm.defaultProps = {
  initialValues: {},
};

export default LiablePartnerForm;
